import { createPortal } from 'react-dom';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { applyTypography } from '../../styled/theme/typography-utils';

/**
 * Tooltip position constants
 * @readonly
 * @enum {string}
 */
export const TOOLTIP_POSITIONS = {
    TOP: 'top',
    BOTTOM: 'bottom',
    LEFT: 'left',
    RIGHT: 'right',
};

const TooltipContent = styled.div`
    position: fixed;
    z-index: 9999;
    background: ${({ theme }) => theme?.components?.lyt_tooltip?.background || 'var(--color-background-neutral-bold-default, #474749)'};
    color: ${({ theme }) => theme?.components?.lyt_tooltip?.text || 'var(--color-text-invert, #F8F9FC)'};
    padding: 4px 8px;
    border-radius: 4px;
    ${applyTypography('0_normal')}
    line-height: 133%; /* Override to match Preset/0/Normal specification */
    pointer-events: none;
    white-space: normal;
    max-width: 244px;
    opacity: ${({ $show }) => ($show ? 1 : 0)};
    visibility: ${({ $show }) => ($show ? 'visible' : 'hidden')};
    transition: opacity 0.1s ease-in-out, visibility 0.1s ease-in-out;
`;

const TooltipTrigger = styled.div`
    display: inline-block;
`;

/**
 * LytTooltip component provides accessible tooltips with consistent styling
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - The element that triggers the tooltip
 * @param {string} props.content - The tooltip text content
 * @param {string} props.position - Tooltip position: 'top', 'bottom', 'left', 'right'
 * @param {boolean} props.forceShow - Force tooltip to be visible (for testing/debugging)
 * @param {number} props.delay - Delay in milliseconds before showing tooltip
 * @param {boolean} props.disabled - Disable tooltip functionality
 * @returns {JSX.Element} The tooltip component
 */
const LytTooltip = ({ 
    children, 
    content, 
    position = 'top', 
    forceShow = false, 
    delay = 200,
    disabled = false 
}) => {
    const [isVisible, setIsVisible] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState({ bottom: 0, left: -200 });
    const triggerRef = useRef(null);
    const showTimeout = useRef(null);
    const tooltipRoot = document.getElementById('tooltip-root');

    const updatePosition = () => {
        if (!triggerRef.current || (!isVisible && !forceShow)) return;

        const rect = triggerRef.current.getBoundingClientRect();
        const tooltipOffset = 12; // Distance from trigger element
        
        const positions = {
            top: {
                top: rect.top - tooltipOffset,
                left: rect.left + (rect.width / 2),
                transform: 'translate(-50%, -100%)'
            },
            bottom: {
                top: rect.bottom + tooltipOffset,
                left: rect.left + (rect.width / 2),
                transform: 'translateX(-50%)'
            },
            left: {
                top: rect.top + (rect.height / 2),
                right: window.innerWidth - rect.left + tooltipOffset,
                transform: 'translateY(-50%)'
            },
            right: {
                top: rect.top + (rect.height / 2),
                left: rect.right + tooltipOffset,
                transform: 'translateY(-50%)'
            },
        };

        setTooltipPosition(positions[position] || positions.top);
    };

    const handleMouseEnter = () => {
        if (disabled) return;
        
        showTimeout.current = setTimeout(() => {
            setIsVisible(true);
        }, delay);
    };

    const handleMouseLeave = () => {
        if (showTimeout.current) {
            clearTimeout(showTimeout.current);
        }
        setIsVisible(false);
    };

    const handleClick = () => {
        // Hide tooltip on click to handle cases where the trigger element moves
        if (showTimeout.current) {
            clearTimeout(showTimeout.current);
        }
        setIsVisible(false);
    };

    const handleFocus = () => {
        if (disabled) return;
        setIsVisible(true);
    };

    const handleBlur = () => {
        setIsVisible(false);
    };

    useLayoutEffect(() => {
        updatePosition();
        window.addEventListener('scroll', updatePosition);
        window.addEventListener('resize', updatePosition);

        return () => {
            if (showTimeout.current) {
                clearTimeout(showTimeout.current);
            }
            window.removeEventListener('scroll', updatePosition);
            window.removeEventListener('resize', updatePosition);
        };
    }, [isVisible, forceShow, position]);

    const shouldShow = (isVisible || forceShow) && !disabled && content;

    // If no content provided, just return children without tooltip functionality
    if (!content) {
        return children;
    }

    return (
        <>
            <TooltipTrigger
                ref={triggerRef}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onClick={handleClick}
                onFocus={handleFocus}
                onBlur={handleBlur}
            >
                {children}
            </TooltipTrigger>
            {tooltipRoot && createPortal(
                <TooltipContent 
                    style={tooltipPosition}
                    $show={shouldShow}
                    role="tooltip"
                    aria-hidden={!shouldShow}
                >
                    {content}
                </TooltipContent>,
                tooltipRoot
            )}
        </>
    );
};

LytTooltip.propTypes = {
    children: PropTypes.node.isRequired,
    content: PropTypes.string,
    position: PropTypes.oneOf(['top', 'bottom', 'left', 'right']),
    forceShow: PropTypes.bool,
    delay: PropTypes.number,
    disabled: PropTypes.bool
};

export default LytTooltip;
